import type { PolygonWithHoles, Ring, Vec2 } from '../geo/types';
import { ensureCCW, polygonArea } from '../geo/polygon';

export interface KeyedOutline {
  readonly regionId: string;
  readonly polygon: PolygonWithHoles;
}

/* Clipper leaves every point it makes on the 1 mm grid: three decimals write a corner exactly. */
const CORNER_DECIMALS = 3;

function isLower(p: Vec2, q: Vec2): boolean {
  return p[0] < q[0] || (p[0] === q[0] && p[1] < q[1]);
}

function lowestAt(ring: Ring): number {
  return ring.reduce((low, p, i) => (isLower(p, ring[low]!) ? i : low), 0);
}

function cornerName(p: Vec2): string {
  return `${p[0].toFixed(CORNER_DECIMALS)},${p[1].toFixed(CORNER_DECIMALS)}`;
}

/* The outline written counter-clockwise from its lowest corner, whatever vertex it starts at. */
function outlineName(ring: Ring): string {
  const turned = ensureCCW(ring);
  const start = lowestAt(turned);
  return [...turned.slice(start), ...turned.slice(0, start)].map(cornerName).join(' ');
}

/* Larger first, then by outline: an order no other piece takes part in. */
function byOutline(a: KeyedOutline, b: KeyedOutline): number {
  const [na, nb] = [outlineName(a.polygon.outer), outlineName(b.polygon.outer)];
  return polygonArea(b.polygon) - polygonArea(a.polygon) || (na < nb ? -1 : na > nb ? 1 : 0);
}

/*
 * A ground piece's key: its region and the lowest corner of its outline, the least x and then the
 * least y. Only the piece's own outline goes into it, so pieces added or cut anywhere else, in its
 * region or in another, leave the key as it was; the partition's order, which any such change
 * shifts, plays no part. Pieces of one region that share that corner are numbered after the first.
 */
export function pieceKeys(pieces: readonly KeyedOutline[]): readonly string[] {
  const bases = pieces.map(
    (piece) =>
      `${piece.regionId}@${cornerName(piece.polygon.outer[lowestAt(piece.polygon.outer)]!)}`
  );
  const groups = bases.reduce(
    (found, base, i) => found.set(base, [...(found.get(base) ?? []), i]),
    new Map<string, readonly number[]>()
  );
  const numbered = new Map(
    [...groups.values()]
      .filter((members) => members.length > 1)
      .flatMap((members) =>
        [...members]
          .sort((i, j) => byOutline(pieces[i]!, pieces[j]!))
          .map((i, rank) => [i, rank === 0 ? bases[i]! : `${bases[i]!}#${rank}`] as const)
      )
  );
  return bases.map((base, i) => numbered.get(i) ?? base);
}
